/**
 * Locale and currency detection utilities
 */

// Countries where service is not available due to legal/regulatory reasons
export const BLOCKED_COUNTRIES = [
  'KP', // North Korea
  'IR', // Iran
  'SY', // Syria
  'CU', // Cuba
  'SD', // Sudan
  'RU', // Russia (temporary due to sanctions)
  'BY', // Belarus (temporary due to sanctions)
];

// Countries that use dollar but not USD
export const NON_USD_DOLLAR_COUNTRIES = [
  { code: 'CA', currency: 'CAD', symbol: 'C$' },
  { code: 'AU', currency: 'AUD', symbol: 'A$' },
  { code: 'NZ', currency: 'NZD', symbol: 'NZ$' },
  { code: 'SG', currency: 'SGD', symbol: 'S$' },
  { code: 'HK', currency: 'HKD', symbol: 'HK$' },
  { code: 'TW', currency: 'TWD', symbol: 'NT$' },
  { code: 'MX', currency: 'MXN', symbol: 'MX$' },
];

// Country to currency mapping for all countries
export const COUNTRY_CURRENCIES: Record<string, string> = {
  US: 'USD', CA: 'CAD', MX: 'MXN', BR: 'BRL',
  GB: 'GBP', DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR',
  NL: 'EUR', BE: 'EUR', AT: 'EUR', CH: 'CHF', SE: 'SEK',
  NO: 'NOK', DK: 'DKK', FI: 'EUR', PL: 'PLN', CZ: 'CZK',
  AU: 'AUD', NZ: 'NZD', JP: 'JPY', CN: 'CNY', KR: 'KRW',
  SG: 'SGD', IN: 'INR', HK: 'HKD', TW: 'TWD',
  // Gulf countries
  AE: 'AED', SA: 'SAR', KW: 'KWD', QA: 'QAR', BH: 'BHD', OM: 'OMR',
  // Others
  ZA: 'ZAR', IL: 'ILS', IE: 'EUR', PT: 'EUR', GR: 'EUR',
  HU: 'HUF', RO: 'RON', HR: 'HRK', BG: 'BGN', LT: 'EUR',
  LV: 'EUR', EE: 'EUR', SI: 'EUR', SK: 'EUR', MT: 'EUR',
  CY: 'EUR', LU: 'EUR', TH: 'THB', MY: 'MYR', ID: 'IDR',
  PH: 'PHP', VN: 'VND'
};

/**
 * The two-letter region of a locale tag: en-US -> US, en_GB -> GB, zh-Hans-CN -> CN, en-US@posix -> US,
 * en_US.UTF-8 -> US. undefined when the tag carries no region (en, C, POSIX).
 */
export function regionFromLocale(locale: string | null | undefined): string | undefined {
  if (!locale) return undefined;
  const subtags = String(locale).split(/[-_@.]/);
  const region = subtags.slice(1).find((t) => /^[A-Za-z]{2}$/.test(t));
  return region ? region.toUpperCase() : undefined;
}

// Detect user's locale and country
export function detectUserLocale() {
  if (typeof window === 'undefined') {
    return { locale: 'en-US', country: 'US', currency: 'USD' };
  }

  // Try multiple detection methods
  const browserLocale = navigator.language || 'en-US';
  const language = browserLocale.split(/[-_@.]/)[0];
  const countryCode = regionFromLocale(browserLocale);
  
  // Try to get country from timezone
  let detectedCountry = countryCode || 'US';
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Map common timezones to countries
    if (timezone.includes('London')) detectedCountry = 'GB';
    else if (timezone.includes('Paris')) detectedCountry = 'FR';
    else if (timezone.includes('Berlin')) detectedCountry = 'DE';
    else if (timezone.includes('Tokyo')) detectedCountry = 'JP';
    else if (timezone.includes('Sydney')) detectedCountry = 'AU';
    else if (timezone.includes('Toronto')) detectedCountry = 'CA';
    else if (timezone.includes('Singapore')) detectedCountry = 'SG';
    else if (timezone.includes('Mumbai') || timezone.includes('Kolkata')) detectedCountry = 'IN';
    else if (timezone.includes('Shanghai') || timezone.includes('Beijing')) detectedCountry = 'CN';
    else if (timezone.includes('Dubai')) detectedCountry = 'AE';
    else if (timezone.includes('Johannesburg')) detectedCountry = 'ZA';
    else if (timezone.includes('Sao_Paulo')) detectedCountry = 'BR';
    else if (timezone.includes('Mexico_City')) detectedCountry = 'MX';
  } catch (e) {
    console.warn('Could not detect country from timezone', e);
  }

  // Determine currency using the comprehensive country-currency mapping
  const currency = COUNTRY_CURRENCIES[detectedCountry] || 'USD';
  const dollarCountry = NON_USD_DOLLAR_COUNTRIES.find(c => c.code === detectedCountry);

  return {
    locale: browserLocale,
    country: detectedCountry,
    currency,
    language,
    isNonUS: detectedCountry !== 'US',
    usesDollarSymbol: !!dollarCountry || detectedCountry === 'US'
  };
}

// Format price with explicit USD notation
export function formatPriceUSD(amount: number, options: {
  showCurrency?: boolean;
  locale?: string;
} = {}) {
  const { showCurrency = true, locale = 'en-US' } = options;
  
  if (showCurrency) {
    return `USD $${amount.toFixed(2)}`;
  }
  
  return `$${amount.toFixed(2)}`;
}

// Estimate currency conversion (for display only - not for actual pricing)
export function estimateLocalCurrency(usdAmount: number, targetCurrency: string): string {
  // Approximate exchange rates (should be updated regularly or fetched from API)
  const rates: Record<string, number> = {
    EUR: 0.92,
    GBP: 0.79,
    CAD: 1.36,
    AUD: 1.53,
    NZD: 1.64,
    SGD: 1.35,
    HKD: 7.85,
    JPY: 149.50,
    CNY: 7.24,
    INR: 83.12,
    BRL: 4.97,
    MXN: 17.05,
    AED: 3.67,
    ZAR: 18.75,
  };

  const rate = rates[targetCurrency];
  if (!rate) return '';

  const converted = usdAmount * rate;
  
  // Format based on currency
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: targetCurrency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return formatter.format(converted);
}

// Check if country is blocked
export function isCountryBlocked(countryCode: string): boolean {
  return BLOCKED_COUNTRIES.includes(countryCode.toUpperCase());
}

// Get user-friendly country name
export function getCountryName(countryCode: string): string {
  const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
  try {
    return regionNames.of(countryCode) || countryCode;
  } catch {
    return countryCode;
  }
}

// Store user's locale preference
export function saveLocalePreference(locale: ReturnType<typeof detectUserLocale>) {
  if (typeof window !== 'undefined') {
    localStorage.setItem('userLocale', JSON.stringify(locale));
  }
}

// Get stored locale preference
export function getStoredLocale(): ReturnType<typeof detectUserLocale> | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem('userLocale');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}
